import { useAsync, useDebouncedEffect } from "@react-hookz/web";
import { useMemo, useState } from "react";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import type { DropdownComponentProps } from "../../components/common/Dropdown";
import type { DropdownItemData } from "../../components/common/DropdownHelpers";
import type { SfObject } from "../../store/insightLinkage";
import { SfObjectType } from "../../store/insightLinkage";
import { notificationStore, NotificationType } from "../../store/notification";
import { isLoading } from "../../utils/api.utils";
import {
    getAdvertiserId,
    getOpportunityId,
    isValidAdvertiserId,
} from "../../utils/permission.utils";
import { hasSfObjectContext } from "../../utils/permission.utils";
import { NOT_CREATED_DROPDOWN_VALUE } from "../../utils/salesforce.utils";
import { DropdownComponent } from "../common/Dropdown";
import { filterItemByValueAndLabel } from "../common/DropdownHelpers";
import * as DropdownDimensionHelpers from "../dimensions/DropdownDimensionHelpers";

export interface OpportunityDropdownProps {
    sfObject: SfObject;
    setSfObject: (sfObjectData: SfObject) => void;
    defaultOpportunity?: DropdownItemData;
    includeClosed?: boolean;
    closeModal?: () => void;
}

export const OpportunityDropdown: React.FunctionComponent<
    OpportunityDropdownProps
> = (props: OpportunityDropdownProps) => {
    const {
        sfObject,
        setSfObject,
        defaultOpportunity,
        includeClosed,
        closeModal,
    } = props;
    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);
    const [filter, setFilter] = useState("");
    const [items, setItems] = useState<DropdownItemData[]>(
        defaultOpportunity ? [defaultOpportunity] : [],
    );
    const sfObjectContextExists = hasSfObjectContext();
    const rpMap = useMemo(
        () => ({
            value: "opportunity_id",
            label: "opportunity_name",
        }),
        [],
    );
    const label = "Salesforce Opportunity";

    const [inputRequestState, inputRequestAction] = useAsync(async () => {
        try {
            // Certain hard-coded sfid values, such as AccountAnosticWithIdentifier break the validation for sfids when we
            // send queries up to SalesForce. To work around this, invalid IDs are replaced with ""
            let advertiserId = getAdvertiserId();
            if (!isValidAdvertiserId()) {
                advertiserId = "";
                console.warn(
                    `Current sfid is invalid to query SalesForce, sending ${advertiserId} instead.`,
                );
            }

            const { data } = await (
                await getCdbApiClient()
            ).getSfOpportunities({
                advertiserId,
                includeClosed,
            });
            const fetchData = DropdownDimensionHelpers.processItemMapper(
                data ?? [],
                rpMap,
            );
            setItems([...items, ...fetchData]);
            const opportunityId = getOpportunityId();
            if (opportunityId) {
                const targetOpportunity = fetchData.find(
                    (item) => item.value === opportunityId,
                );
                if (targetOpportunity) {
                    setSfObject({
                        sfObjectId: opportunityId,
                        sfObjectType: SfObjectType.OPPORTUNITY,
                    });
                }
            }
            if (
                sfObjectContextExists &&
                sfObject.sfObjectType === SfObjectType.OPPORTUNITY
            ) {
                const targetSFObjectId = fetchData.find(
                    (item) => item.value === sfObject.sfObjectId,
                );

                if (!targetSFObjectId) {
                    upsertNotification(
                        "Invalid opportunity selected",
                        NotificationType.ERROR,
                    );
                    closeModal?.();
                }
            }
        } catch (err: any) {
            upsertNotification(err.message, NotificationType.ERROR);
        }
    });

    useDebouncedEffect(
        () =>
            void (async () => {
                await inputRequestAction.execute();
            })(),
        [],
        300,
    );

    const filteredItems = useMemo(
        () => filterItemByValueAndLabel(items, filter),
        [filter, inputRequestState],
    );

    const dropdownProps: DropdownComponentProps = {
        dimension: "opportunity",
        label,
        items: filteredItems,
        search: filter,
        onChange: (e) => {
            if (e === NOT_CREATED_DROPDOWN_VALUE) {
                setSfObject({
                    sfObjectId: "",
                    sfObjectType: SfObjectType.ACTIVITY,
                });
                return;
            }

            const { value: opportunityIdLookup } = items.find(
                (item) => item.value === e,
            ) ?? { value: e };

            setSfObject({
                sfObjectId: opportunityIdLookup,
                sfObjectType: SfObjectType.OPPORTUNITY,
            });
        },

        selectedValue:
            sfObject.sfObjectType === SfObjectType.OPPORTUNITY
                ? sfObject.sfObjectId
                : NOT_CREATED_DROPDOWN_VALUE,

        setSearch: (e) => {
            setFilter(e);
        },
        loading: isLoading(inputRequestState.status),
        showItemsWhileLoading: inputRequestState.status === "loading",
        disabled: sfObjectContextExists,
    };

    return <DropdownComponent {...dropdownProps} />;
};

export default OpportunityDropdown;
